import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Обработчик с постоянной ссылкой, который при вызове видит свежие пропсы и состояние.
 *
 * <p><b>Зачем.</b> `PanResponder.create` и построители жестов gesture-handler принимают
 * колбэки один раз — при создании. Пересоздавать их на каждое изменение пропсов нельзя: у
 * `PanResponder` вместе с объектом уходит накопленное состояние жеста, и лист, который
 * тянут пальцем, прыгнул бы на место, стоило родителю перерисоваться. А замкнуть колбэк на
 * первый рендер — значит навсегда оставить в нём тот `onClose`, что был тогда.
 *
 * <p>Это то же, что `useEffectEvent`, но для обработчиков событий: функцию можно отдавать в
 * жесты и в пропсы. Звать её во время рендера нельзя — до коммита в ней прежняя версия.
 */
export function useEventCallback<Args extends unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  const fnRef = useRef(fn);
  useLayoutEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  return useCallback((...args: Args) => fnRef.current(...args), []);
}
